import { useEffect, useRef, useState, type ReactNode } from 'react';
import { RefreshCw } from '@vitness/icons';

const THRESHOLD = 72;   // px of pull before release triggers
const MAX = 110;        // resistance ceiling

/**
 * `app_settings` key holding the opt-in. Absent — which is the state of every
 * install until someone turns it on — reads as off.
 */
export const PULL_TO_REFRESH_KEY = 'pull_to_refresh';

/**
 * Pull-to-refresh — the mobile refresh verb.
 *
 * Wraps the routed content. On touchstart it walks up from the touch target to
 * find the actual scroll container (the shell owns it, and which element
 * scrolls is a layout detail this component shouldn't be coupled to); a pull
 * only begins if that container is at the top. Movement past a small slop is
 * given square-root resistance so the indicator feels attached, and release
 * past the threshold runs `onRefresh` with the spinner held visible until it
 * resolves.
 *
 * Touch-only by design — desktop has the sync button, and mice don't pull.
 *
 * `enabled` is off unless the caller says otherwise, and the caller scopes it
 * to the Dashboard. The gesture spends the same downward movement as an
 * ordinary scroll, which is a fair trade on a page you only read and would
 * plausibly want fresher — and a bad one on Settings or a form, where the drag
 * fires while you're reaching for a control. When off, this is a plain
 * passthrough: no listeners, no wrapper transform.
 */
export function PullToRefresh({ enabled = false, onRefresh, children }: {
  enabled?: boolean;
  onRefresh: () => Promise<unknown>;
  children: ReactNode;
}) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const state = useRef<{ y0: number; scroller: Element | null; active: boolean } | null>(null);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    const scrollerFor = (el: Element | null): Element | null => {
      for (let n = el; n; n = n.parentElement) {
        if (n.scrollHeight > n.clientHeight + 1) {
          const oy = getComputedStyle(n).overflowY;
          if (oy === 'auto' || oy === 'scroll') return n;
        }
      }
      return document.scrollingElement;
    };

    const onStart = (e: TouchEvent) => {
      if (busyRef.current) return;
      const scroller = scrollerFor(e.target as Element);
      const top = scroller ? scroller.scrollTop : 0;
      state.current = { y0: e.touches[0].clientY, scroller, active: top <= 0 };
    };

    const onMove = (e: TouchEvent) => {
      const s = state.current;
      if (!s?.active || busyRef.current) return;
      const dy = e.touches[0].clientY - s.y0;
      if (dy <= 8) { setPull(0); return; }
      /*
       * Claim the gesture once it's past the slop.
       *
       * Without this the browser keeps running its own overscroll alongside the
       * transform below, so the content travels twice: once because we moved it
       * and once because the page rubber-banded under it. `overscroll-behavior-y:
       * none` on <body> suppresses that on Chrome but not reliably in iOS
       * Safari, which is the platform this gesture exists for.
       *
       * The listener has to be non-passive for `preventDefault` to do anything —
       * a passive listener's call is ignored with a console warning. That costs
       * nothing here: the handler only runs at all once `active` is set, which
       * requires the scroller to have been at the top on touchstart.
       */
      if (e.cancelable) e.preventDefault();
      // Square-root resistance: the further you pull, the less it moves.
      setPull(Math.min(MAX, Math.sqrt(dy - 8) * 7));
    };

    const onEnd = () => {
      const s = state.current;
      state.current = null;
      if (!s?.active) return;
      setPull(current => {
        if (current >= THRESHOLD && !busyRef.current) {
          busyRef.current = true;
          setBusy(true);
          onRefresh().finally(() => {
            busyRef.current = false;
            setBusy(false);
          });
          return THRESHOLD * 0.75; // hold the indicator while refreshing
        }
        return 0;
      });
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    // Non-passive on purpose — see onMove. touchstart/touchend stay passive:
    // neither cancels anything, and passive listeners are cheaper to schedule.
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      // A route change or a flipped toggle mid-gesture would otherwise leave
      // the content parked mid-pull with nothing left to release it.
      state.current = null;
      setPull(0);
    };
  }, [enabled, onRefresh]);

  if (!enabled) return <>{children}</>;

  const shown = busy ? THRESHOLD * 0.75 : pull;
  const ready = pull >= THRESHOLD;

  return (
    // Marked so "is the gesture armed on this route?" is answerable from the
    // DOM — the wrapper is otherwise indistinguishable from any other
    // positioned div in the shell.
    <div data-pull-to-refresh="" style={{ position: 'relative' }}>
      {shown > 0 && (
        <div aria-hidden={!busy} role={busy ? 'status' : undefined} style={{
          position: 'absolute', top: shown - 40, left: 0, right: 0, zIndex: 5,
          display: 'flex', justifyContent: 'center', pointerEvents: 'none',
          opacity: Math.min(1, shown / THRESHOLD),
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--vn-surface-raised)', border: '1px solid var(--vn-border-strong)',
            boxShadow: 'var(--vn-shadow-dropdown)',
            color: ready || busy ? 'var(--vn-accent-safe)' : 'var(--vn-text-muted)',
          }}>
            <RefreshCw size={15} style={{
              transform: busy ? undefined : `rotate(${shown * 2.2}deg)`,
              animation: busy ? 'vd-spin 1s linear infinite' : undefined,
            }} />
          </span>
        </div>
      )}
      <div style={{
        transform: shown > 0 ? `translateY(${shown}px)` : undefined,
        transition: state.current ? undefined : 'transform .2s ease',
      }}>
        {children}
      </div>
    </div>
  );
}
